import type {
  AddOrUpdateItemType,
  BasketItem,
  BasketItemUpdateData,
} from '@scayle/storefront-nuxt'
import { ExistingItemHandling, extendPromise } from '@scayle/storefront-nuxt'
import { useI18n } from '#i18n'
import { useToast } from '~/composables/useToast'
import { useTrackingEvents } from '~/composables/useTrackingEvents'
import { useBasket, useLog } from '#storefront/composables'
import { useApplyPromotions } from '#storefront-promotions/composables/useApplyPromotions'
import { getBasketToastErrorMessage } from '~/utils/toats'

export type AddToBasketItem = AddOrUpdateItemType & {
  productName: string
  interval?: string
  existingItemHandling?: ExistingItemHandling
  customData?: Record<string, any>
}

export type UseBasketActionsReturn = {
  /** A function which adds an item to the basket and shows a success or error toast. */
  addItem: (item: AddToBasketItem) => Promise<void>
  /** A function which removes an item from the basket and shows a success or error toast. It also triggers a `remove_from_cart` tracking event.‚ */
  removeItem: (
    item: BasketItem,
    context?: { location?: string },
  ) => Promise<void>
  /** A function which updates the quantity of the passed basket item and shows a success or error toast. It triggers `add_to_cart` when quantity increases or `remove_from_cart` when quantity decreases.  */
  updateItemQuantity: (item: BasketItem, newQuantity: number) => Promise<void>
}

/**
 * A composable to make it easier to add, update and remove items from the basket.
 * In addition of interacting with the basket, it also takes care of tracking and displaying toast messages.
 *
 * @returns An {@link UseBasketActionsReturn} object containing functions to add, update and remove items from the basket.
 */

export function useBasketActions(): UseBasketActionsReturn &
  Promise<UseBasketActionsReturn> {
  const i18n = useI18n()
  const log = useLog()

  const { show } = useToast()

  const { trackRemoveFromBasket, trackAddToBasket } = useTrackingEvents()

  const basket = useBasket()
  const { removeItemByKey, addItem: addItemToBasket, updateItem } = basket
  const { applyPromotions } = useApplyPromotions({ basket: basket.data })

  const removeItem = async (
    { key, product, quantity, variant }: BasketItem,
    context?: { location?: string },
  ) => {
    await removeItemByKey(key)
    await applyPromotions()
    trackRemoveFromBasket({
      product,
      quantity,
      variant,
      ...(context?.location && { location: context.location }),
    })
  }

  const addItem = async (item: AddToBasketItem) => {
    try {
      const existingItemHandling =
        item.existingItemHandling ||
        ExistingItemHandling.ADD_QUANTITY_TO_EXISTING
      await addItemToBasket({
        ...item,
        customData: {
          ...item.customData,
          shopVersion: 'NH_v2',
        },
        existingItemHandling,
      })
      await applyPromotions(basket.data)
    } catch (error) {
      log.error('Item could not be added to basket', error)
      show(getBasketToastErrorMessage(error, i18n, item.productName), {
        type: 'ERROR',
        action: 'CONFIRM',
      })
      throw error
    }
  }

  const updateItemQuantity = async (
    basketItem: BasketItem,
    newQuantity: number,
  ) => {
    const currentQuantity = basketItem.quantity

    await updateItem(basketItem.key, {
      quantity: newQuantity,
      customData: basketItem?.customData as BasketItemUpdateData['customData'],
      displayData: basketItem?.displayData,
      itemGroup: basketItem?.itemGroup,
      promotionId: basketItem?.promotionId,
    })
    await applyPromotions(basket.data)

    // Track the appropriate event based on quantity change
    if (newQuantity > currentQuantity) {
      // Quantity increased - track as add_to_cart
      trackAddToBasket({
        product: basketItem.product,
        variant: basketItem.variant,
        quantity: newQuantity - currentQuantity, // Track only the difference
        actionType: 'change_quantity',
        location: 'cart page',
      })
    } else if (newQuantity < currentQuantity) {
      // Quantity decreased - track as remove_from_cart
      trackRemoveFromBasket({
        product: basketItem.product,
        variant: basketItem.variant,
        quantity: currentQuantity - newQuantity, // Track only the difference
      })
    }
    // If newQuantity === currentQuantity, don't track anything (shouldn't happen in practice)
  }

  return extendPromise(
    basket.then(() => ({})),
    {
      removeItem,
      addItem,
      updateItemQuantity,
    },
  )
}
